/* @flow */
import React, { useCallback, useMemo } from "react";
import styled, { css } from "styled-components";
import { scaleThreshold } from "d3-scale";
import Media from "react-media";
import Gettext from "../common/Gettext";
import Spinner from "../Spinners/SimpleSpinner";
import * as Helpers from "./NextGenChartMath";
import IntervalsModal, { HumanInterval } from "../TradeModals/IntervalsModal";
import { getTimeUntilNextLockToggle } from "../../redux/reducer/stocks";
import { formatTimeInterval } from "../TradeModals/TradeIsClosing";
import { Timer } from "../Toolbar/Icons";

import { rgba } from "polished";
import { fontSize } from "../common/styled-mixins";

import type { Stock } from "../../redux/CommonTypes";
import { gaReducer } from "../GA";

const Container = styled.div`
  display: flex;
  justify-content: center;
  height: 30px;
  margin-bottom: 10px;

  @media (min-width: 960px) {
    padding-left: 25px;
  }

  @media (max-width: 959px) {
    position: relative;
    height: 26px;
    margin-left: 16px;
  }
`;

const TimeContainer = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  justify-content: center;
`;

const ControlsContainer = styled.div`
  width: 92px;
  min-width: 92px;
  margin-left: 10px;
  display: flex;
  justify-content: center;

  > button {
    width: 30px;
    min-width: 30px;
    margin-left: 1px;
    font-size: 0;
    line-height: 0;

    :first-of-type {
      margin-left: 0;
    }
  }

  @media (max-width: 959px) {
    width: auto;
    min-width: auto;
    margin-left: 4px;
  }
`;

const Button = styled.button`
  width: 100%;
  min-width: 0;
  padding: 0 5px;
  font-size: ${fontSize(14, 14)};
  font-weight: bold;
  color: ${({ theme }) => rgba(theme.foreground, 0.5)};
  background-color: ${rgba("#919191", 0.59)};
  white-space: nowrap;
  border: none;
  cursor: pointer;

  @media (max-width: 959px) {
    font-size: ${fontSize(10, 10)};
  }

  :first-of-type {
    border-radius: 4px 0 0 4px;
  }

  :last-of-type {
    border-radius: 0 4px 4px 0;
  }

  :hover {
    color: ${({ theme }) => theme.foreground};
  }

  ${props =>
    props.candle &&
    css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-left: 12px;
      padding-right: 8px;
      font-weight: 400;
      text-transform: none;
      border-radius: 4px !important;
    `};

  ${props =>
    props.candleMobile &&
    css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-right: 4px;
      padding-left: 6px;
      padding-right: 8px;
      font-weight: 400;
      font-size: ${fontSize(12)};
      text-transform: none;
      border-radius: 4px !important;
    `};

  ${props =>
    props.active &&
    css`
      color: #ffffff;
      background-color: ${({ theme }) => theme.bottomNavigationBg};
      &:hover {
        color: #ffffff;
      }
    `};

  @media (max-width: 959px) {
    ${props =>
      props.minus &&
      css`
        position: absolute;
        top: -41px;
        left: 0;
        width: 26px;
        height: 26px;
        border-radius: 4px !important;
        z-index: 10;
      `};

    ${props =>
      props.plus &&
      css`
        position: absolute;
        top: -41px;
        right: 16px;
        width: 26px;
        height: 26px;
        border-radius: 4px !important;
        z-index: 10;
      `};

    ${props =>
      props.now &&
      css`
        border-radius: 4px !important;
      `};
  }
`;

const SpinnerContainer = styled.div`
  width: 25px;
  min-width: 25px;
  align-self: center;
  margin-left: 5px;
  font-size: 16px;

  @media (max-width: 959px) {
    margin-left: 2px;
    margin-right: 1px;
    width: 13px;
    min-width: 13px;
    font-size: ${fontSize(12)};
  }
`;

const CandleButton = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: center;
  margin-left: 10px;
  min-width: 120px;

  @media (max-width: 959px) {
    margin-left: 0;
    min-width: auto;
  }
`;

const CandleImage = styled.div`
  margin-right: 5px;
`;

const ArrowImage = styled.div`
  margin-left: 5px;
  font-size: 0;
  line-height: 0;
  transform-origin: 50% 50%;
  transition: transform 0.15s linear;

  ${props =>
    props.active &&
    css`
      transform: rotate(180deg);
    `};
`;

const ClockText = styled.div`
  position: absolute;
  left: 0;
  bottom: 100%;
  transform: translateY(-10px);
  padding: 0 8px;
  font-size: ${fontSize(14)};
  background-color: ${rgba("#919191", 0.59)};
  color: ${({ theme }) => theme.foreground};
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: -1;
  pointer-events: none;

  :after {
    content: "";
    position: absolute;
    top: 100%;
    left: 9px;
    border: 7px solid transparent;
    border-top: 7px solid ${rgba("#919191", 0.59)};
    border-bottom: none;
  }
`;

const ClockContainer = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: center;
  margin-right: 10px;
  font-weight: 400;
  font-size: ${fontSize(10)};
  line-height: 27px;
  white-space: nowrap;
  transform: translateZ(0);
  z-index: 5;

  @media (max-width: 959px) {
    color: ${({ theme }) => theme.foreground};
    margin-right: 4px;
  }

  ${props =>
    props.oldClock &&
    css`
      position: absolute;
      left: 27px;
      bottom: 12px;

      @media (max-width: 959px) {
        left: 12px;
      }
    `};

  &:hover ${ClockText} {
    opacity: 1;
    z-index: 1;
  }
`;

const Clock = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
  text-transform: none;
  font-weight: 400;
  color: ${({ theme }) => theme.foreground};
  border-radius: 4px !important;
  cursor: default;

  ${props =>
    props.oldClock &&
    css`
      background-color: ${props => props.theme.ticksFontColor};
    `};

  ${props =>
    props.timeOtc &&
    css`
      color: ${({ theme }) => theme.chartTimeOtcColor};
      background-color: ${({ theme }) => theme.accent};

      &:hover {
        color: ${({ theme }) => theme.chartTimeOtcColor};
      }
    `};

  @media (max-width: 959px) {
    font-size: ${fontSize(12)};
    padding: 0 7px;
  }
`;

const ClockIcon = styled.div`
  margin-right: 7px;
  font-size: 0;
  line-height: 0;
  color: ${({ theme }) => rgba(theme.foreground, 0.4)};

  ${props =>
    props.timeOtc &&
    css`
      color: ${({ theme }) => theme.chartTimeOtcColor};
    `};

  @media (max-width: 959px) {
    display: none;
  }
`;

type Props = {
  social?: React.Node,
  stock: Stock,
  loading: boolean,
  timeSpan: number,
  onTimeSpanChange: (
    ({ timeSpan: number, animateTimeSpan: boolean }) => {
      timeSpan: number,
      animateTimeSpan: boolean
    }
  ) => void,
  onBackToReality: () => void,
  chartType: "line" | "candlestick" | "bar",
  interval: number,
  onIntervalChange: number => void,
  isShownCandleModal: boolean,
  toggleCandleModal: () => void,
  now: number
};

export function TheClock(props: {
  children: number,
  oldClock: boolean,
  otc: boolean
}) {
  return (
    <Media
      query="(min-width: 480px)"
      render={() => (
        <ClockContainer oldClock={props.oldClock}>
          <Clock as="div" timeOtc={props.otc} oldClock={props.oldClock}>
            <ClockIcon timeOtc={props.otc}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="-5651.999 5637.688 15.997 20.001"
                width="16"
                height="20"
                fill="currentColor"
              >
                <path
                  d="M1238-12037.673a7.953,7.953,0,0,1,2.35-5.649l-.9-1.264a.493.493,0,0,1,.118-.692l1.318-.932a.5.5,0,0,1,.7.118l.889,1.252a7.939,7.939,0,0,1,2.262-.717v-.514a.6.6,0,0,0-.6-.6h-.33a.6.6,0,0,1-.6-.6v-1.823a.6.6,0,0,1,.6-.6h4.385a.6.6,0,0,1,.6.6v1.823a.6.6,0,0,1-.6.6h-.33a.6.6,0,0,0-.6.6v.514a7.854,7.854,0,0,1,2.12.65l.9-1.268a.5.5,0,0,1,.7-.117l1.318.934a.5.5,0,0,1,.121.693l-.877,1.233a7.948,7.948,0,0,1,2.462,5.758,8,8,0,0,1-8,7.983A8,8,0,0,1,1238-12037.673Zm1.92,0a6.078,6.078,0,0,0,6.078,6.066,6.081,6.081,0,0,0,6.081-6.066,6.083,6.083,0,0,0-6.081-6.069A6.081,6.081,0,0,0,1239.922-12037.673Zm6.039-.042v-4.96l.039,0a5.016,5.016,0,0,1,5.014,4.963Z"
                  transform="translate(-6890 17687.379)"
                />
              </svg>
            </ClockIcon>
            {formatTimeInterval(props.children)}
          </Clock>
          <Media
            query="(min-width: 960px)"
            render={() => (
              <ClockText timeOtc={props.otc} oldClock={props.oldClock}>
                <Gettext>left till the market closure</Gettext>
              </ClockText>
            )}
          />
        </ClockContainer>
      )}
    />
  );
}

function DesktopCandleIntervalPicker(props) {
  return (
    <CandleButton>
      {props.active && (
        <IntervalsModal
          value={props.value}
          onChange={props.onChange}
          onToggle={props.onToggle}
          stock={props.stock}
        />
      )}
      <Button candle active={props.active} onClick={props.onToggle}>
        <CandleImage>
          <Timer />
        </CandleImage>
        <HumanInterval>{Helpers.stringifyTime(props.value)}</HumanInterval>
        <ArrowImage active={props.active}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 9.8 6.295"
            fill="currentColor"
            width="9.8"
            height="6.3"
          >
            <path d="M5,.328l4.9,4.9-1.4,1.4L5,3.119l-3.5,3.5L.1,5.219,4.986.328,5,.337Z" />
          </svg>
        </ArrowImage>
      </Button>
    </CandleButton>
  );
}

function MobileCandleIntervalPicker(props) {
  const MemoCandleButton = useMemo(() => {
    return (
      <CandleButton>
        <Button candleMobile active={props.active} onClick={props.onToggle}>
          <CandleImage>
            <Timer />
          </CandleImage>
          {Helpers.stringifyTime(props.value)}
          <ArrowImage active={props.active}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 9.8 6.295"
              fill="currentColor"
              width="9.8"
              height="6.3"
            >
              <path d="M5,.328l4.9,4.9-1.4,1.4L5,3.119l-3.5,3.5L.1,5.219,4.986.328,5,.337Z" />
            </svg>
          </ArrowImage>
        </Button>
      </CandleButton>
    );
  }, [props.active, props.onToggle, props.value]);
  return MemoCandleButton;
}

const ChartBottomNavigation = (props: Props) => {
  // Возможность зума ограничена текущим выбранным интервалом свечей. Мы рассчи-
  // тываем показать пять вариантов масштаба на выбор, но на некоторых интерва-
  // лах количество кнопок придётся уменьшить.
  let timeSpanRange = Helpers.getTimeSpanRange(props.stock, props.interval);

  const candleTimeSpans = [
    props.interval * 10,
    props.interval * 20,
    props.interval * 50,
    props.interval * 75,
    props.interval * 100
  ].filter(t => t >= timeSpanRange[0] && t <= timeSpanRange[1]);

  const timeSpans =
    props.chartType === "line"
      ? Helpers.isShortHistoryStock(props.stock)
        ? ["2m", "10m", "20m", "45m", "1h", "2h"].map(Helpers.parseTime)
        : ["2m", "1h", "4h", "1D", "1W", "1M"].map(Helpers.parseTime)
      : candleTimeSpans;

  // Если тебе всегда хотелось узнать, как выглядит оверинжениринг,
  // то вот он. Здесь мы используем d3.scaleThreshold() для того,
  // чтобы выбитать наиболее близкий к произвольному значению зум
  // из списка предустановленнызх значений!
  // range это просто список индексов, но т.к. наш набор отсортиро-
  // ван в обратную сторону, мы его разворачиваем.
  // domain это набор из N-1 зачения зумов. Каждое значение это среднее
  // арифметическое меджду двумя соседними зумами.
  // Такие дела.
  const approximateTimeSpan = scaleThreshold()
    .domain(
      timeSpans.reduce((acc, item, index, input) => {
        if (index < input.length - 1) {
          acc.push((item + input[index + 1]) / 2);
        }

        return acc;
      }, [])
    )
    .range(timeSpans.map((item, index) => index));

  const activeTimeSpanIndex = approximateTimeSpan(props.timeSpan);
  const timeUntilToggle = getTimeUntilNextLockToggle(props.now, props.stock);

  const memoDesktopCandleIntervalPicker = useCallback(
    () => (
      <DesktopCandleIntervalPicker
        active={props.isShownCandleModal}
        onToggle={props.toggleCandleModal}
        value={props.interval}
        onChange={props.onIntervalChange}
        stock={props.stock}
      />
    ),
    [
      props.isShownCandleModal,
      props.toggleCandleModal,
      props.interval,
      props.onIntervalChange,
      props.stock
    ]
  );

  const memoGAMinus = useCallback(() => {
    gaReducer("CLICKBTN_TIME_MINUS", {
      stock: props.stock.stock
    });
    props.onTimeSpanChange(prev => ({
      timeSpan: prev.timeSpan * 1.5,
      animateTimeSpan: true
    }));
  }, [props.stock, props.onTimeSpanChange]);

  const memoGAPlus = useCallback(() => {
    gaReducer("CLICKBTN_TIME_PLUS", {
      stock: props.stock.stock
    });
    props.onTimeSpanChange(prev => ({
      timeSpan: prev.timeSpan / 1.5,
      animateTimeSpan: true
    }));
  }, [props.stock, props.onTimeSpanChange]);

  const memoGATimeNow = useCallback(() => {
    gaReducer("CLICKBTN_TIME_NOW", {
      stock: props.stock.stock
    });
    props.onBackToReality();
  }, [props.stock, props.onBackToReality]);

  const memoScaleButtons = useMemo(
    () => (
      <ControlsContainer>
        <Button onClick={memoGAMinus} minus>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 12 2"
            width="12"
            height="2"
            fill="currentColor"
          >
            <rect width="12" height="2" />
          </svg>
        </Button>

        <Button onClick={memoGAPlus} plus>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 12 12"
            width="12"
            height="12"
            fill="currentColor"
          >
            <path d="M5,12V7H0V5H5V0H7V5h5V7H7v5Z" />
          </svg>
        </Button>

        <Button onClick={memoGATimeNow} now title="Go back to now">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 18 18"
            width="18"
            height="18"
            fill="currentColor"
          >
            <path d="M8,18V15.929A7.017,7.017,0,0,1,2.071,10H0V8H2.071A7.017,7.017,0,0,1,8,2.071V0h2V2.071A7.017,7.017,0,0,1,15.929,8H18v2H15.929A7.017,7.017,0,0,1,10,15.929V18Zm0-3.084V12h2v2.916A6.015,6.015,0,0,0,14.916,10H12V8h2.916A6.016,6.016,0,0,0,10,3.083V6H8V3.083A6.017,6.017,0,0,0,3.083,8H6v2H3.083A6.016,6.016,0,0,0,8,14.916ZM8,9a1,1,0,1,1,1,1A1,1,0,0,1,8,9Z" />
          </svg>
        </Button>
      </ControlsContainer>
    ),
    [memoGAMinus, memoGAPlus, memoGATimeNow]
  );

  return (
    <Container>
      {props.chartType !== "line" && (
        <Media
          query="(max-width: 959px)"
          render={() => (
            <MobileCandleIntervalPicker
              active={props.isShownCandleModal}
              onToggle={props.toggleCandleModal}
              value={props.interval}
              onChange={props.onIntervalChange}
              stock={props.stock}
            />
          )}
        />
      )}

      {props.social}

      {timeUntilToggle < 1000 * 60 * 60 * 12 && (
        <TheClock otc={props.stock.otc} oldClock={false}>
          {timeUntilToggle}
        </TheClock>
      )}

      {props.chartType === "line" ? (
        <TimeContainer>
          {[...timeSpans].reverse().map((item, index, spans) => (
            <Button
              key={item}
              active={index === spans.length - 1 - activeTimeSpanIndex}
              onClick={e => {
                gaReducer("CLICKBTN_CHART_TIME", {
                  buttonText: e.currentTarget.textContent,
                  stock: props.stock.stock
                });
                props.onTimeSpanChange(() => ({
                  timeSpan: item,
                  animateTimeSpan: true
                }));
              }}
            >
              {index === 0
                ? Helpers.stringifyTime(Math.max(item, props.timeSpan))
                : index === spans.length - 1
                ? Helpers.stringifyTime(Math.min(item, props.timeSpan))
                : Helpers.stringifyTime(item)}
            </Button>
          ))}
        </TimeContainer>
      ) : (
        <TimeContainer>
          {[...timeSpans].reverse().map((timeSpan, index, set) => (
            <Button
              key={index}
              active={activeTimeSpanIndex === set.length - 1 - index}
              onClick={e => {
                gaReducer("CLICKBTN_CHART_TIME", {
                  buttonText: e.currentTarget.textContent,
                  stock: props.stock.stock
                });
                props.onTimeSpanChange(() => ({
                  timeSpan: timeSpan,
                  animateTimeSpan: true
                }));
              }}
            >
              {Helpers.stringifyTime(timeSpan)}
            </Button>
          ))}
        </TimeContainer>
      )}

      {props.chartType !== "line" && (
        <Media
          query="(min-width: 960px)"
          render={memoDesktopCandleIntervalPicker}
        />
      )}
      {memoScaleButtons}
      <SpinnerContainer>{props.loading && <Spinner />}</SpinnerContainer>
    </Container>
  );
};

export default ChartBottomNavigation;



// WEBPACK FOOTER //
// ./src/components/Chart/ChartBottomNavigation.js